import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { UserPlus, Loader2 } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  UniversalEmptyState,
  Card,
  Button,
  Input,
  StatusBadge,
  type StatusTone,
  type UniversalTableColumn,
  SearchField,
} from '@sighthost/ui';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import {
  salesService,
  type StaffMember,
  type SalesCompany,
  type AgentInviteStatus,
} from '@/domains/sales/services/salesService';
import { useStaffRole } from '@/shared/hooks/useStaffRole';

const INVITE_TONE: Record<AgentInviteStatus, StatusTone> = {
  active: 'active',
  accepted: 'info',
  invited: 'pending',
  pending: 'expiring',
  deactivated: 'draft',
};
const INVITE_LABEL: Record<AgentInviteStatus, string> = {
  active: 'Active',
  accepted: 'Accepted',
  invited: 'Invite sent',
  pending: 'Not delivered',
  deactivated: 'Deactivated',
};

/**
 * SalesAgentsPage — manager+ view of sales staff in scope.
 * Includes invite form. Deactivate flow opens a modal that requires a reassign target.
 */

export default function SalesAgentsPage() {
  const { companyId, isAdmin } = useStaffRole();
  const navigate = useNavigate();
  const [rows, setRows] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await salesService.listAgents(isAdmin ? undefined : companyId ?? undefined);
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [companyId, isAdmin]);

  useEffect(() => { reload(); }, [reload]);

  /**
   * SEARCH ONLY — no date control, deliberately.
   *
   * §4.2 classified this as a small roster with no date question, and the
   * available dates say why: `hired_at` and `invited_at` are HR facts about a
   * person, not a window anyone scopes a roster by. "Agents hired last quarter"
   * is a report, not a way to find Diana.
   *
   * Search earns its place because rosters grow — an external partner company
   * can carry twenty agents — and a name is exactly how you look one up.
   */
  const table = useTableFilters<StaffMember>({
    rows,
    searchText: (r) => [r.display_name, r.email ?? '', r.phone ?? '', r.role].join(' '),
  });

  const columns = useMemo<UniversalTableColumn<StaffMember>[]>(() => [
    { key: 'display_name', header: 'Name', render: (r) => <span className="font-medium">{r.display_name}</span> },
    { key: 'email', header: 'Email', render: (r) => <span className="text-muted-foreground">{r.email ?? '—'}</span> },
    { key: 'role', header: 'Role', render: (r) => <StatusBadge tone={r.role === 'admin' ? 'overdue' : 'info'}>{r.role.replace('sales_', '').replace('_', ' ')}</StatusBadge> },
    { key: 'invite_status', header: 'Invite', render: (r) => <StatusBadge tone={INVITE_TONE[r.invite_status]}>{INVITE_LABEL[r.invite_status]}</StatusBadge> },
    { key: 'hired_at', header: 'Hired', render: (r) => r.hired_at ? new Date(r.hired_at).toLocaleDateString('en-ZA') : '—' },
  ], []);

  return (
    <UniversalPage
      title="Agents"
      description={isAdmin ? 'All companies' : 'Your company'}
      actions={
        <Button onClick={() => setShowInvite((v) => !v)} variant="primary">
          <UserPlus className="h-4 w-4" /> {showInvite ? 'Cancel invite' : 'Invite agent'}
        </Button>
      }
    >
      {showInvite && <InviteForm companyId={companyId} isAdmin={isAdmin} onDone={() => { setShowInvite(false); reload(); }} />}
      {loading ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Loading…</Card>
      ) : rows.length === 0 ? (
        <UniversalEmptyState title="No agents yet" description="Invite your first agent above." />
      ) : (
        <>
          <div className="mb-3 flex justify-end">
            <SearchField
              value={table.q}
              onValueChange={table.setQ}
              pending={table.isSearching}
              placeholder="Search name, email, role"
              className="w-full max-w-xs"
            />
          </div>
          <UniversalTable
            columns={columns}
            data={table.rows}
            getRowKey={(r) => r.id}
            caption={table.caption}
            onRowClick={(r) => navigate(`/sales/agents/${r.id}`)}
          />
        </>
      )}
    </UniversalPage>
  );
}

function InviteForm({ companyId, isAdmin, onDone }: { companyId: string | null; isAdmin: boolean; onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<'sales_agent' | 'sales_manager'>('sales_agent');
  const [adminCompanyId, setAdminCompanyId] = useState('');
  const [companies, setCompanies] = useState<SalesCompany[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Admin sees a dropdown of companies — replaces the old "paste a UUID" UX.
  useEffect(() => {
    if (!isAdmin) return;
    salesService.listCompanies().then((data) => {
      setCompanies(data);
      if (data.length > 0 && !adminCompanyId) setAdminCompanyId(data[0]!.id);
    }).catch(() => { /* surface inside submit if it matters */ });
  }, [isAdmin]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (isAdmin && !adminCompanyId) {
      setError('Pick a company.');
      return;
    }
    setSubmitting(true);
    try {
      await salesService.inviteStaff(email, displayName, role, isAdmin ? adminCompanyId : undefined);
      onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card className="mb-4 p-4">
      <form onSubmit={onSubmit} className="grid gap-3 sm:grid-cols-2">
        <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Display name" required />
        <Input value={email} type="email" onChange={(e) => setEmail(e.target.value)} placeholder="Email" required />
        <select className="rounded-md border border-input bg-background px-3 py-2 text-sm" value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
          <option value="sales_agent">Sales agent</option>
          <option value="sales_manager">Sales manager</option>
        </select>
        {isAdmin && (
          <select
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={adminCompanyId}
            onChange={(e) => setAdminCompanyId(e.target.value)}
            required
          >
            <option value="" disabled>Select company…</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{c.active ? '' : ' (inactive)'}
              </option>
            ))}
          </select>
        )}
        {error && <div className="sm:col-span-2 rounded-md border border-danger/30 bg-danger/5 p-2 text-xs text-danger">{error}</div>}
        <div className="sm:col-span-2">
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Send invite
          </Button>
        </div>
      </form>
    </Card>
  );
}
